/**
 * Password breach checking utility using HaveIBeenPwned API
 * Implements k-anonymity to protect user privacy
 */

interface BreachCheckResult {
  breached: boolean;
  count?: number;
  error?: string;
}

/**
 * Check if a password has been breached using HaveIBeenPwned API
 * Uses k-anonymity by only sending first 5 characters of SHA-1 hash
 */
export async function checkPasswordBreach(password: string): Promise<BreachCheckResult> {
  try {
    // Create SHA-1 hash of the password
    const hash = await sha1(password);
    const hashPrefix = hash.substring(0, 5).toUpperCase();
    const hashSuffix = hash.substring(5).toUpperCase();

    // Query HaveIBeenPwned API with k-anonymity
    const response = await fetch(`https://api.pwnedpasswords.com/range/${hashPrefix}`, {
      method: 'GET',
      headers: {
        'User-Agent': 'TechAdds-Registration-Security'
      }
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.text();
    const lines = data.split('\n');

    // Find our hash suffix in the response
    for (const line of lines) {
      const [suffix, count] = line.trim().split(':');
      if (suffix === hashSuffix) {
        return {
          breached: true,
          count: parseInt(count, 10)
        };
      }
    }

    return { breached: false };
  } catch (error) {
    console.warn('Password breach check failed:', error);
    return {
      breached: false,
      error: 'Unable to verify password security'
    };
  }
}

/**
 * Create SHA-1 hash of a string
 */
async function sha1(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-1', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Debounced password breach checking to avoid excessive API calls
 */
let breachCheckTimeout: NodeJS.Timeout | null = null;
let lastCheckedPassword = '';
let lastResult: BreachCheckResult | null = null;

export function debouncedBreachCheck(
  password: string,
  callback: (result: BreachCheckResult) => void,
  delay: number = 1000
): void {
  // Don't check if password is too short or same as last check
  if (password.length < 8 || password === lastCheckedPassword) {
    if (lastResult) {
      callback(lastResult);
    }
    return;
  }

  // Clear previous timeout
  if (breachCheckTimeout) {
    clearTimeout(breachCheckTimeout);
  }

  // Set new timeout
  breachCheckTimeout = setTimeout(async () => {
    lastCheckedPassword = password;
    lastResult = await checkPasswordBreach(password);
    callback(lastResult);
  }, delay);
}